import { ReactElement, ReactNode, useCallback, useMemo } from 'react';
import { useToast } from '@cad-arc';
import { AppName, AppNameProvider } from '@m43/core';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { observer } from 'mobx-react-lite';
import { AdditionalInfosProviderCore, ShowErrorAlertProvider } from '../../../additional-infos';
import { AdditionalInfoType } from '../../../additional-infos/types';
import { useCurrentUser } from '../../../current-user/current-user';
import { useCadNextAdditionalInfoNotifications } from '../../../event-notifications/use-additional-info-notifications';
import { useBoloPanelMetadata } from '../state/bolo-panel-metadata-context';

export const BoloPanelAdditionalInfosProvider = observer(
    ({ children }: { children: ReactNode }): ReactElement => {
        const metadata = useBoloPanelMetadata();
        const { userId } = useCurrentUser();
        const notificationManager = useCadNextAdditionalInfoNotifications();
        const toast = useToast();
        const noSystemMessagesByDefault = useEnabledFeatures()?.[
            'filterOutSystemMessagesFromNarrativeByDefault'
        ];

        const showErrorAlert = useCallback(
            ({ message: description }) => {
                toast({
                    description,
                    status: 'error',
                });
            },
            [toast]
        );

        const initialFilters = useMemo(() => {
            const initialFilters = [AdditionalInfoType.USER];

            if (!noSystemMessagesByDefault) {
                initialFilters.push(AdditionalInfoType.SYSTEM);
            }

            return initialFilters;
        }, [noSystemMessagesByDefault]);

        if (metadata?.boloId === undefined) {
            // eslint-disable-next-line react/jsx-no-useless-fragment
            return <>{children}</>;
        }

        return (
            <AppNameProvider appName={AppName.CAD_NEXT}>
                <ShowErrorAlertProvider showErrorAlert={showErrorAlert}>
                    <AdditionalInfosProviderCore
                        isBolo
                        entityId={metadata?.boloId}
                        entityNumber={metadata?.boloNumber}
                        currentUserId={userId}
                        notificationManager={notificationManager}
                        initialFilter={initialFilters}
                    >
                        {children}
                    </AdditionalInfosProviderCore>
                </ShowErrorAlertProvider>
            </AppNameProvider>
        );
    }
);
